import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  AlertTriangle, 
  RefreshCw, 
  Home, 
  Bug,
  ExternalLink
} from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  attemptedRecovery: boolean;
}

export class DashboardErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      attemptedRecovery: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    console.error('Dashboard Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Send error to analytics service (if available)
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'exception', {
        description: error.toString(),
        fatal: false,
      });
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      attemptedRecovery: true
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReload = () => {
    window.location.reload();
  };

  handleReportError = () => {
    const { error } = this.state;
    const errorReport = {
      message: error?.message,
      stack: error?.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    };
    
    // Create mailto link with error details
    const subject = encodeURIComponent('Dashboard Error Report');
    const body = encodeURIComponent(`
Error Report:
${JSON.stringify(errorReport, null, 2)}

Steps to reproduce:
1. 
2. 
3. 

Additional context:

    `);
    
    window.open(`mailto:support@example.com?subject=${subject}&body=${body}`);
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isNetworkError = this.state.error?.message.includes('NetworkError') || 
                           this.state.error?.message.includes('fetch');
      
      const isJavaScriptError = this.state.error?.name === 'SyntaxError' ||
                              this.state.error?.name === 'ReferenceError';

      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full space-y-6">
            {/* Main Error Card */}
            <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
              <CardHeader className="text-center">
                <div className="mx-auto h-16 w-16 bg-red-50 rounded-xl flex items-center justify-center mb-4">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
                <CardTitle className="text-2xl font-bold text-slate-900">
                  Something went wrong
                </CardTitle>
                <CardDescription className="text-slate-600 text-lg">
                  The dashboard encountered an unexpected error. Don't worry - your data is safe.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Error Type Alert */}
                <Alert variant={isNetworkError ? "default" : "destructive"} className="border-slate-200">
                  <Bug className="h-4 w-4" />
                  <AlertTitle>
                    {isNetworkError ? 'Network Error' : 
                     isJavaScriptError ? 'Script Error' : 'Application Error'}
                  </AlertTitle>
                  <AlertDescription>
                    {isNetworkError ? 
                      'Unable to connect to the server. Please check your internet connection.' :
                      this.state.error?.message || 'An unexpected error occurred in the application.'
                    }
                  </AlertDescription>
                </Alert>

                {/* Recovery Suggestions */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-2">What you can try:</h3>
                  <ul className="text-slate-700 text-sm space-y-1">
                    <li>• Refresh the page to reload the application</li>
                    <li>• Clear your browser cache and cookies</li>
                    <li>• Try accessing the dashboard in an incognito/private window</li>
                    <li>• Check your internet connection</li>
                    {this.state.attemptedRecovery && (
                      <li>• Contact support if the problem persists</li>
                    )}
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Button 
                    onClick={this.handleRetry}
                    className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800"
                    size="lg"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </Button>
                  
                  <Button 
                    onClick={this.handleReload}
                    variant="outline"
                    className="flex items-center gap-2 hover:bg-slate-50 border-slate-200"
                    size="lg"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Reload Page
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Button 
                    onClick={this.handleGoHome}
                    variant="outline"
                    className="flex items-center gap-2 hover:bg-slate-50 border-slate-200"
                    size="lg"
                  >
                    <Home className="h-4 w-4" />
                    Go to Home
                  </Button>
                  
                  <Button 
                    onClick={this.handleReportError}
                    variant="outline"
                    className="flex items-center gap-2 hover:bg-slate-50 border-slate-200"
                    size="lg"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Report Error
                  </Button>
                </div>

                {/* Technical Details (Collapsible) */}
                {process.env.NODE_ENV === 'development' && (
                  <details className="mt-6">
                    <summary className="cursor-pointer text-sm font-medium text-slate-600 hover:text-slate-900">
                      Show technical details
                    </summary>
                    <div className="mt-3 p-4 bg-slate-900 rounded-lg text-white text-xs font-mono">
                      <div className="mb-2 text-slate-300">Error:</div>
                      <div className="mb-4">{this.state.error?.message}</div>
                      {this.state.error?.stack && (
                        <>
                          <div className="mb-2 text-slate-300">Stack trace:</div>
                          <div className="whitespace-pre-wrap">{this.state.error.stack}</div>
                        </>
                      )}
                    </div>
                  </details>
                )}
              </CardContent>
            </Card>

            {/* Support Information */}
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="p-6 text-center">
                <p className="text-sm text-slate-600 mb-3">
                  If this problem persists, please contact our support team.
                </p>
                <div className="flex flex-wrap justify-center gap-4 text-xs text-slate-500">
                  <span>Error ID: {Date.now().toString(36)}</span>
                  <span>Time: {new Date().toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC for wrapping components with error boundary
export function withDashboardErrorBoundary<P extends object>(
  Component: React.ComponentType<P>
) {
  return function WithErrorBoundary(props: P) {
    return (
      <DashboardErrorBoundary>
        <Component {...props} />
      </DashboardErrorBoundary>
    );
  };
}

// Hook for manually triggering error boundary
export function useErrorBoundary() {
  const throwError = (error: Error) => {
    throw error;
  };
  
  return throwError;
} 